import { pension, high3, mra, birthYearOf, serviceAt, exactAge, dateAtAge, SURVIVOR_COST, deferredStartAge, fersSupplement } from "@/lib/fers";

function finish(p, o) {
  const life = Number(p.life_expectancy) || 90;
  const suppYears = o.supplement > 0 ? Math.max(0, 62 - o.suppStart) : 0;
  const lifetime = o.eligible ? o.annual * Math.max(0, life - o.startAge) + o.supplement * suppYears : 0;
  return { ...o, monthly: o.annual / 12, suppMonthly: o.supplement / 12, lifetime, startDate: o.eligible ? dateAtAge(p, o.startAge) : null };
}

function context(p, age) {
  const m = mra(birthYearOf(p));
  const S = serviceAt(p) + Math.max(0, age - exactAge(p));
  const fers = p.retirement_system === "FERS";
  return {
    p, age, m, S, fers,
    base: (high3({ ...p, retire_age: age }) * S) / 100,
    surv: SURVIVOR_COST[p.survivor_benefit || "none"],
    supp: fers ? fersSupplement(p, S) : 0,
    tax: (Number(p.tax_rate) || 0) / 100,
    common: { service: S, mra: m, mraDate: dateAtAge(p, m), separateDate: dateAtAge(p, age), separateAge: age },
  };
}

export function regularOption({ p, age, common }) {
  const reg = pension({ ...p, retire_age: age });
  return { ...common, key: "regular", label: "Regular", eligible: reg.eligible, startAge: reg.startAge, annual: reg.annual, reduction: reg.reduction,
    supplement: reg.supplement, suppStart: age, penalty: reg.reduction > 0, rule: reg.type, notes: reg.notes };
}

export function veraOption({ age, m, S, fers, base, surv, supp, common }) {
  const ok = fers && ((age >= 50 && S >= 20) || S >= 25);
  return { ...common, key: "vera", label: "VERA", eligible: ok, startAge: age, annual: ok ? base * (1 - surv) : 0, reduction: 0,
    supplement: ok && age < 62 ? supp : 0, suppStart: Math.max(age, m), penalty: false,
    rule: ok ? "Voluntary Early Retirement: immediate annuity, no age penalty" : fers ? "Needs age 50 with 20+ years, or any age with 25+ years" : "VERA estimate is modeled for FERS only",
    notes: ok ? [`The FERS Supplement starts at your MRA (${m.toFixed(1)}) and stops at 62.`, "Only available when your agency offers a VERA window. Keeps FEHB if you've had 5 years of coverage."] : [] };
}

export function vsipOption({ age, m, S, fers, base, surv, tax, common }, vsipAmount) {
  const start = deferredStartAge(age, S, m);
  const ok = fers && S >= 5;
  const why = S >= 30 ? "MRA with 30+ yrs" : S >= 20 ? "60 with 20+ yrs" : "62 with 5+ yrs";
  return { ...common, key: "vsip", label: "VSIP (deferred)", eligible: ok, startAge: start, annual: ok ? base * (1 - surv) : 0, reduction: 0,
    supplement: 0, suppStart: start, penalty: false, lump: vsipAmount, lumpNet: vsipAmount * (1 - tax),
    rule: ok ? `Leave at ${age.toFixed(0)} with a VSIP; deferred annuity starts at ${start.toFixed(1)} (${why})` : fers ? "A deferred annuity needs at least 5 years of service" : "VSIP estimate is modeled for FERS only",
    notes: ok ? ["No FERS Supplement on a deferred annuity.", "FEHB and FEGLI end at separation and can't be restored with a deferred annuity (a postponed MRA+10 with 10+ years can restore FEHB).",
      `VSIP lump sum is taxable; about ${Math.round(tax * 100)}% withheld at your assumed rate. Taking a VSIP and returning to federal work within 5 years requires repaying it.`] : [] };
}

export const mra10Reduction = (age, S, m) => (S >= 30 || (age >= 60 && S >= 20) ? 0 : Math.min(1, 0.05 * (62 - age)));

export function mra10Option({ p, age, m, S, fers, base, surv, supp, common }) {
  const ok = fers && age >= m && S >= 10;
  const unreduced = S >= 30 || (age >= 60 && S >= 20);
  const red = ok ? mra10Reduction(age, S, m) : 0;
  const postpone = ok && red > 0 ? [60, 62].filter((a) => a > age && (a === 62 || S >= 20)).map((a) => ({ startAge: a, date: dateAtAge(p, a), reduction: 0, monthly: (base * (1 - surv)) / 12 })) : [];
  const rule = ok ? (red > 0 ? `Immediate annuity reduced ${Math.round(red * 100)}% (5% per year under 62), permanently` : "Immediate unreduced annuity") : fers ? `Needs your MRA (${m.toFixed(1)}) and 10+ years of service` : "MRA+10 applies to FERS only";
  return { ...common, key: "mra10", label: "MRA+10", eligible: ok, startAge: age, annual: ok ? base * (1 - red) * (1 - surv) : 0, reduction: red,
    supplement: ok && unreduced && age < 62 ? supp : 0, suppStart: age, penalty: red > 0, postpone, rule,
    notes: ok && red > 0 ? ["Postpone the start to shrink or remove the reduction; FEHB resumes when the annuity starts if you had 5 years of coverage."] : [] };
}

export function earlyOptions(p, age, vsipAmount = 25000) {
  const ctx = context(p, age);
  return [regularOption(ctx), veraOption(ctx), vsipOption(ctx, vsipAmount), mra10Option(ctx)].map((o) => finish(p, o));
}
